import { GAMIFICATION_ROUTES } from "./gamificationRoutes";
import React, { useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import HomeDashboardLayout from "../HomeDashboardLayout";

import "../../dashboard/Dashboard.css";
import "../HomeDashboard.css";
import "../questionBank/QuestionBankList.css";
import "../quizzes/QuizLeaderboard.css";
import "../rapidFire/RapidFireLeaderBoard.css";
import HeaderProfileSection from "../HeaderProfileSection";
import MyWalletSidebar from "../MyWalletSidebar";
import { useGamificationBattleLeaderboard } from "../../hooks/useGamificationBattleLeaderboard";
import { useSidebarData } from "../../hooks/useSidebarData";
import {
  buildGamificationBackTarget,
  buildGamificationFlowState,
  getStoredGamificationFlowState,
  persistGamificationFlowState,
} from "./gamificationFlowState";

import leaderboardLeft from "../../assets/images/profile1.png";
import leaderboardCenter from "../../assets/images/expert1.png";
import leaderboardRight from "../../assets/images/c1.png";
import crown from "../../assets/images/crown1.png";

function getTopPlayerByRank(topThree = [], rank) {
  return topThree.find((player) => Number(player?.rank) === Number(rank)) || null;
}

function getTopPlayerBadge(rank) {
  if (rank === 1) return leaderboardCenter;
  if (rank === 2) return leaderboardLeft;
  return leaderboardRight;
}

function GamificationBattleLeaderboardShimmer() {
  return (
    <div className="leaderboard-screen" aria-hidden="true">
      <div className="leaderboard-header">
        <div className="event-info">
          <span className="dashboard-skeleton dashboard-skeleton--line qbl-shimmer__title" />
          <span className="dashboard-skeleton dashboard-skeleton--line qbl-shimmer__date" />
        </div>
      </div>

      <div className="leaderboard-content">
        <section className="leaderboard-card">
          <div className="top-three qbl-shimmer__topThree">
            {Array.from({ length: 3 }).map((_, index) => (
              <div className="top-player qbl-shimmer__topPlayer" key={index}>
                <span className="dashboard-skeleton dashboard-skeleton--thumb qbl-shimmer__topBadge" />
                <span className="dashboard-skeleton dashboard-skeleton--line qbl-shimmer__topName" />
                <span className="dashboard-skeleton dashboard-skeleton--button qbl-shimmer__topScore" />
              </div>
            ))}
          </div>

          <div className="rank-header">
            <span className="rank-heading">RANK LIST</span>
            <span className="performer-heading">Top Performers</span>
          </div>

          <div className="rank-list-wrapper">
            <div className="rank-list">
              {Array.from({ length: 10 }).map((_, index) => (
                <div className="rank-row qbl-shimmer__row" key={index}>
                  <span className="dashboard-skeleton dashboard-skeleton--line qbl-shimmer__rank" />
                  <span className="dashboard-skeleton dashboard-skeleton--thumb qbl-shimmer__avatar" />
                  <span className="dashboard-skeleton dashboard-skeleton--line qbl-shimmer__name" />
                  <span className="dashboard-skeleton dashboard-skeleton--button qbl-shimmer__score" />
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

const GamificationBattleLeaderBoard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { data: sidebarData = null } = useSidebarData();
  const flowState = useMemo(
    () => buildGamificationFlowState(location.state, getStoredGamificationFlowState()),
    [location.state]
  );
  const selectedAttempt = flowState?.selectedAttempt || {};
  const battlesId = Number(
    flowState?.battles_id ||
      selectedAttempt?.battlesId ||
      selectedAttempt?.battles_id ||
      selectedAttempt?.raw?.battles_id ||
      flowState?.selectedBattle?.battles_id ||
      flowState?.selectedBattle?.battle_id ||
      0
  );
  const battleRoomId = Number(
    flowState?.battle_room_id ||
      selectedAttempt?.battleRoomId ||
      selectedAttempt?.battle_room_id ||
      selectedAttempt?.raw?.battle_room_id ||
      0
  );
  const resolvedBackTarget = useMemo(
    () =>
      flowState?.gamificationBackTarget?.pathname
        ? flowState.gamificationBackTarget
        : buildGamificationBackTarget(GAMIFICATION_ROUTES.answerSheet, flowState),
    [flowState]
  );

  useEffect(() => {
    persistGamificationFlowState(flowState);
  }, [flowState]);

  const goBack = () => {
    navigate(resolvedBackTarget.pathname, {
      replace: true,
      state: resolvedBackTarget.state || {},
    });
  };

  useEffect(() => {
    window.history.pushState({ gamificationBattleLeaderboardGuard: true }, "", window.location.href);

    const handlePopState = () => {
      goBack();
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [resolvedBackTarget]);

  const leaderboardQuery = useGamificationBattleLeaderboard(
    {
      battles_id: battlesId,
      battle_room_id: battleRoomId,
    },
    {
      enabled: battlesId > 0 && battleRoomId > 0,
    }
  );

  const currentUserId = Number(
    sidebarData?.user_data?.user_id ||
      sidebarData?.user_data?.id ||
      sidebarData?.user?.user_id ||
      sidebarData?.user?.id ||
      0
  );
  const topThree = leaderboardQuery.data?.topThree || [];
  const players = leaderboardQuery.data?.players || [];
  const roomCode = leaderboardQuery.data?.roomCode || "";
  const firstPlayer = getTopPlayerByRank(topThree, 1);
  const secondPlayer = getTopPlayerByRank(topThree, 2);
  const thirdPlayer = getTopPlayerByRank(topThree, 3);
  const currentStudent = players.find((player) => Number(player.studentId) === currentUserId) || null;

  return (
    <>
      <style>{`
         .homeDashboard-wrapper .page-content {
          padding-right: 0;
          min-height: 0;
          height: calc(100vh - 35px);
          overflow-x: hidden;
        }

        .homeDashboard-wrapper .main-content {
          height: 100vh;
          overflow-x: hidden !important;
          box-sizing: border-box;
          padding-bottom: 0px;
          min-height: 0;
          display: flex;
          flex-direction: column;
        }

        .layout-wrapper {
          background: #f8f9fa !important;
        }

        .page-padding {
          padding: 30px 25px 0 !important;
        }

       @media (max-width: 767px){
        .page-padding {
             padding: 30px 15px 0 !important;
        }
      `}</style>
      <HomeDashboardLayout showHeader={false}>
        <div className="qbl-page qbankList-page page-padding fixed-page-btn rank-leader">
          <div className="start-quiz qbl-main instruction-main">
            <div className="hideProfile-page ">
              <div className="categories-page-header mb-0">
                <div>
                  <button
                    type="button"
                    className="categories-back-button"
                    onClick={goBack}
                  >
                    <i className="bi bi-chevron-left"></i>
                  </button>
                </div>
                <div>
                  <h4 className="d-flex categories-page-title"></h4>
                </div>
                <div>
                  <HeaderProfileSection />
                </div>
              </div>
            </div>

            <div className="leaderboard-screen">
                  <div>
                <h1 className="qb-title">
                  Leaderboard
                </h1>

                <p className="qb-sub">
                  See how you rank among top learners.
                </p>
              </div>

              {leaderboardQuery.isLoading ? (
                <GamificationBattleLeaderboardShimmer />
              ) : (
                <>
                  <div className="leaderboard-header">
                    <div className="event-info">
                      <h1 className="event-title">Battle Leaderboard</h1>
                      {roomCode ? <span className="event-date coderoom">{`Room Code: ${roomCode}`}</span> : null}
                    </div>
                  </div>

                  <div className="leaderboard-content">
                    <section className="leaderboard-card">
                      <div className="top-three">
                        {secondPlayer ? (
                          <div className="top-player second">
                            <div className="badge-wrapper">
                              <img
                                src={secondPlayer.avatar || getTopPlayerBadge(2)}
                                alt=""
                                className="top-badge"
                                onError={(e) => { e.target.src = getTopPlayerBadge(2); }}
                              />
                              <span className="rank-circle">2</span>
                            </div>
                            <div className="player-name">{secondPlayer.name}</div>
                            <div className="player-score">{secondPlayer.score}</div>
                          </div>
                        ) : (
                          <div className="top-player second" />
                        )}

                        {firstPlayer ? (
                          <div className="top-player first">
                             <span className="winner-crown">
                                 <img src={crown} alt="" />
                             </span>
                            <div className="badge-wrapper">
                              <img
                                src={firstPlayer.avatar || getTopPlayerBadge(1)}
                                alt=""
                                className="top-badge"
                                onError={(e) => { e.target.src = getTopPlayerBadge(1); }}
                              />
                              <span className="rank-circle">1</span>
                              {/* <span className="winner-star"><span>*</span></span> */}
                            </div>
                            <div className="player-name">{firstPlayer.name}</div>
                            <div className="player-score">{firstPlayer.score}</div>
                          </div>
                        ) : (
                          <div className="top-player first" />
                        )}

                        {thirdPlayer ? (
                          <div className="top-player third">
                            <div className="badge-wrapper">
                              <img
                                src={thirdPlayer.avatar || getTopPlayerBadge(3)}
                                alt=""
                                className="top-badge"
                                onError={(e) => { e.target.src = getTopPlayerBadge(3); }}
                              />
                              <span className="rank-circle grey">3</span>
                            </div>
                            <div className="player-name">{thirdPlayer.name}</div>
                            <div className="player-score">{thirdPlayer.score}</div>
                          </div>
                        ) : (
                          <div className="top-player third" />
                        )}
                      </div>

                    

                      <div className="rank-list-wrapper">
                          <div className="rank-header">
                        <span className="rank-heading">RANK LIST</span>
                        <span className="performer-heading">Top Performers</span>
                      </div>
                        <div className="rank-list">
                          {players.map((player) => (
                            <div className="rank-row" key={`${player.rank}-${player.studentId}`}>
                              <div className="rank-number">{player.rank}</div>
                              <img
                                className="small-badge"
                                src={player.avatar || leaderboardCenter}
                                alt=""
                                onError={(e) => { e.target.src = leaderboardCenter; }}
                              />
                              <div className="rank-name">{player.name}</div>
                              <div className="rank-score">{player.score}</div>
                            </div>
                          ))}
                        </div>

                        {currentStudent ? (
                          <div className="rank-bottom-action">
                            <div className="current-rank-card">
                              <div className="current-rank-number">
                                {currentStudent.rank}
                              </div>
                              <div className="current-rank-avatar">
                                <img
                                  src={currentStudent.avatar || leaderboardCenter}
                                  alt=""
                                  onError={(e) => { e.target.src = leaderboardCenter; }}
                                />
                              </div>
                              <div className="current-rank-info">
                                <span>Your Current rank</span>
                                <strong>{currentStudent.name}</strong>
                              </div>
                              <div className="current-rank-score">
                                {currentStudent.score}
                              </div>
                            </div>
                          </div>
                        ) : null}
                      </div>
                    </section>
                  </div>
                </>
              )}
            </div>
          </div>

          <MyWalletSidebar withTopOffset={false} />
        </div>
      </HomeDashboardLayout>
    </>
  );
};

export default GamificationBattleLeaderBoard;
